import * as GC from '@mescius/spread-sheets';

type NormalizedCellBounds = {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
};

/**
 *
 * @param row
 * @param col
 * @param range
 * @returns
 */
export function isCellInRange(
  row: number,
  col: number,
  range: GC.Spread.Sheets.Range
): boolean {
  return (
    row >= range.row &&
    row < range.row + range.rowCount &&
    col >= range.col &&
    col < range.col + range.colCount
  );
}

/**
 *
 * @param sheet
 * @param row
 * @param col
 * @returns
 */
export function getCellBoundsWithSpan(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): NormalizedCellBounds {
  const spans = sheet.getSpans(undefined, GC.Spread.Sheets.SheetArea.viewport);

  for (const span of spans) {
    if (isCellInRange(row, col, span)) {
      return {
        startRow: span.row,
        startCol: span.col,
        endRow: span.row + span.rowCount - 1,
        endCol: span.col + span.colCount - 1,
      };
    }
  }

  return {
    startRow: row,
    startCol: col,
    endRow: row,
    endCol: col,
  };
}

/**
 * Range同士の交差を判定する
 * @param a
 * @param b
 * @returns
 */
export function isIntersectingRange(
  a: GC.Spread.Sheets.Range,
  b: GC.Spread.Sheets.Range
): boolean {
  const aEndRow = a.row + a.rowCount - 1;
  const aEndCol = a.col + a.colCount - 1;
  const bEndRow = b.row + b.rowCount - 1;
  const bEndCol = b.col + b.colCount - 1;

  return !(
    aEndRow < b.row ||
    bEndRow < a.row ||
    aEndCol < b.col ||
    bEndCol < a.col
  );
}

/**
 *
 * @param inner
 * @param outer
 * @returns
 */
export function isRangeContainedInRange(
  inner: GC.Spread.Sheets.Range,
  outer: GC.Spread.Sheets.Range
): boolean {
  const innerEndRow = inner.row + inner.rowCount - 1;
  const innerEndCol = inner.col + inner.colCount - 1;

  const outerEndRow = outer.row + outer.rowCount - 1;
  const outerEndCol = outer.col + outer.colCount - 1;

  return (
    inner.row >= outer.row &&
    inner.col >= outer.col &&
    innerEndRow <= outerEndRow &&
    innerEndCol <= outerEndCol
  );
}

/**
 *
 * @param sheet
 * @param range
 * @returns
 */
export function normalizeRange(
  sheet: GC.Spread.Sheets.Worksheet,
  range: GC.Spread.Sheets.Range
): GC.Spread.Sheets.Range {
  const rowStart = range.row === -1 ? 0 : range.row;
  const rowCount = range.row === -1 ? sheet.getRowCount() : range.rowCount;

  const colStart = range.col === -1 ? 0 : range.col;
  const colCount = range.col === -1 ? sheet.getColumnCount() : range.colCount;

  return new GC.Spread.Sheets.Range(rowStart, colStart, rowCount, colCount);
}

/**
 *
 * @param row
 * @param col
 * @returns
 */
export function toCellAddress(row: number, col: number): string {
  return GC.Spread.Sheets.CalcEngine.rangeToFormula(
    new GC.Spread.Sheets.Range(row, col, 1, 1)
  ).replace(/\$/g, '');
}

/**
 *
 * @param startRow
 * @param startCol
 * @param endRow
 * @param endCol
 * @returns
 */
export function toRangeAddress(
  startRow: number,
  startCol: number,
  endRow: number,
  endCol: number
): string {
  const start = toCellAddress(startRow, startCol);
  const end = toCellAddress(endRow, endCol);

  return start === end ? start : `${start}:${end}`;
}

/**
 *
 * @param sheet
 * @param endRow
 * @param endCol
 * @returns
 */
export function getApiRangeEnd(
  sheet: GC.Spread.Sheets.Worksheet,
  endRow: number,
  endCol: number
): { endRow: number; endCol: number } {
  const bounds = getCellBoundsWithSpan(sheet, endRow, endCol);

  return {
    endRow: bounds.endRow,
    endCol: bounds.endCol,
  };
}

/**
 * APIリクエストに使う範囲の終点は、結合セルの場合最後尾を取るため、変換用の関数を用意している
 * @param sheet
 * @param startRow
 * @param startCol
 * @param endRow
 * @param endCol
 * @returns
 */
export function toApiRangeAddress(
  sheet: GC.Spread.Sheets.Worksheet,
  startRow: number,
  startCol: number,
  endRow: number,
  endCol: number
): string {
  const normalizedEnd = getApiRangeEnd(sheet, endRow, endCol);

  return toRangeAddress(
    startRow,
    startCol,
    normalizedEnd.endRow,
    normalizedEnd.endCol
  );
}
